import * as api from '@apis/feeds-v2';
import * as feedsApi from '@apis/feeds';
import * as campaignsApi from '@apis/campaigns';
import { EditFeedDataView } from '@components/feed/EditFeedDataView';
import { ProductCategoryItem } from '@components/feed/ProductCategoryItem';
import BannerEdit, { BannerEditHandle } from '@components/feed/BannerEdit';
import CalendarEventsEdit from '@components/calendar-events/CalendarEventsEdit';
import SelectProductModal from '@components/product/SelectProductModal';
import SelectProductCollectionModal from '@components/product-collection/SelectProductCollectionModal';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Campaign } from '@models/campaign';
import { Feed, FeedData, FeedDataItem, ProductRecommendationWeights } from '@models/feed';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { removeTempProperties } from '@utils/dataTransform';
import { Button, InputNumber, Modal, Select, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { DeepLinkActions } from '@models/common';
import { DragOutlined } from '@ant-design/icons';

const TAB_TYPE_DESCRIPTIONS: { [key: string]: string } = {
  vote: '(자동) 투표 최신글 10개 노출',
  review: '(자동) 베스트 후기 최신글 10개 랜덤 노출',
  talk: '(자동) 톡톡 최신글 10개 랜덤 노출',
  event: '(자동) 혜택과 참여 배너 노출',
  collection: '데이터 없음',
  product: '데이터 없음',
};

export const ShortcutFeedEdit: React.FC = () => {
  const [shortcutFeed, setShortcutFeed] = useState<Feed<FeedDataItem>>();
  const [modifiedShortcutFeed, setModifiedShortcutFeed] = useState<Feed<FeedDataItem>>();
  const [shortcutModifiedFiles, setShortcutModifiedFiles] = useState<{ [key: string]: File }>({});
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number>(-1);
  const [shortcutModalMode, setShortcutModalMode] = useState<string>();
  const [editedShortcutData, setEditedShortcutData] = useState<Partial<FeedData<FeedDataItem>>>();
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();

  const feedQuery = useQuery({
    queryKey: ['feed-main-feed'],
    queryFn: () => feedsApi.getFeed('main-feed'),
  });

  const updateMainFeedMutation = useMutation({
    mutationFn: feedsApi.updateMainFeed,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feed-main-feed'] });
      dialog.open({ type: 'ok', title: '알림', content: '숏컷을 수정했습니다.' });
      setShortcutModifiedFiles({});
    },
    onError: async (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e.message || JSON.stringify(e) });
    },
  });

  useEffect(() => {
    if (feedQuery.data?.data) {
      const feedData = feedQuery.data.data;
      // curation-shortcut 타입만 필터링
      const shortcutItems = (feedData.data || []).filter((item) => item.type === 'curation-shortcut');
      const shortcutFeedData: Feed<FeedDataItem> = {
        ...feedData,
        data: shortcutItems,
      };
      setShortcutFeed(shortcutFeedData);
      setModifiedShortcutFeed(shortcutFeedData);
    }
  }, [feedQuery.data]);

  const shortcutFeedContent = (modifiedShortcutFeed?.data || shortcutFeed?.data || []).filter((item) => item);

  const handleShortcutSave = async (updatedShortcutData?: FeedData<FeedDataItem>[]) => {
    // 업데이트된 데이터가 있으면 사용, 없으면 현재 modifiedShortcutFeed 사용
    const shortcutDataToSave = updatedShortcutData || modifiedShortcutFeed?.data || [];

    if (!shortcutDataToSave.length && !modifiedShortcutFeed) return;

    // 전체 피드 데이터를 가져와서 curation-shortcut만 업데이트
    const feedData = feedQuery.data?.data;
    if (!feedData) return;

    const allFeedData = feedData.data || [];
    const nonShortcutItems = allFeedData.filter((item) => item.type !== 'curation-shortcut');
    const updatedFeedData: Feed<FeedDataItem> = {
      ...feedData,
      data: [...nonShortcutItems, ...shortcutDataToSave],
    };

    const formData: FormData = new FormData();
    const json = { ...updatedFeedData };
    json.data = (json.data || []).map((item) => {
      if (['curation-class-general', 'curation-class-list', 'curation-class-grid'].includes(item.type)) {
        return { ...item, data: undefined };
      } else {
        return item;
      }
    }) as FeedData<FeedDataItem>[];

    json.data = removeTempProperties(json.data);

    formData.append('json', JSON.stringify(json));
    Object.values(shortcutModifiedFiles).forEach((file) => {
      formData.append('files', file);
    });

    await updateMainFeedMutation.mutateAsync(formData);
  };

  return (
    <>
      <Modal
        width={800}
        open={!!shortcutModalMode}
        onCancel={() => {
          setShortcutModalMode(undefined);
          setEditedShortcutData(undefined);
        }}
        footer={
          <div>
            <Button
              loading={updateMainFeedMutation.isPending}
              onClick={async () => {
                if (!editedShortcutData) return;

                let newData: FeedData<FeedDataItem>[];
                if (shortcutModalMode === 'edit' && modifiedShortcutFeed) {
                  newData = [...shortcutFeedContent];
                  newData[selectedShortcutIndex] = editedShortcutData as FeedData<FeedDataItem>;
                } else if (shortcutModalMode === 'new' && modifiedShortcutFeed) {
                  newData = [...shortcutFeedContent];
                  newData.push(editedShortcutData as FeedData<FeedDataItem>);
                } else {
                  return;
                }

                // state 업데이트
                setModifiedShortcutFeed({ ...modifiedShortcutFeed, data: newData });

                // 바로 저장
                try {
                  await handleShortcutSave(newData);
                  setShortcutModalMode(undefined);
                  setEditedShortcutData(undefined);
                } catch (error) {
                  // 에러는 mutation의 onError에서 처리됨
                }
              }}>
              {shortcutModalMode === 'new' ? '추가' : '수정'}
            </Button>
            <Button
              onClick={() => {
                setShortcutModalMode(undefined);
                setEditedShortcutData(undefined);
              }}>
              취소
            </Button>
          </div>
        }>
        {shortcutModalMode && (
          <EditFeedDataView
            mode={shortcutModalMode}
            data={
              editedShortcutData ||
              (selectedShortcutIndex >= 0 && shortcutFeedContent[selectedShortcutIndex]) || {
                type: 'curation-shortcut',
              }
            }
            onChangeImageFile={(files) => {
              setShortcutModifiedFiles((prev) => {
                return { ...prev, ...files };
              });
            }}
            onChange={(data) => {
              setEditedShortcutData(data);
            }}
          />
        )}
      </Modal>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>숏컷 설정(검색화면)</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={() => {
                setSelectedShortcutIndex(0);
                setShortcutModalMode('edit');
              }}>
              수정
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

interface SortableTabItemProps {
  tab: FeedData<FeedDataItem>;
  index: number;
  handleTabChange: (index: number, field: 'title' | 'type', value: string) => void;
  handleDeleteTab: (index: number) => void;
  handleRemoveDataItem: (tabIndex: number, dataIndex: number) => void;
  setCurrentTabIndex: (index: number) => void;
  setIsProductModalOpen: (open: boolean) => void;
  setIsCollectionModalOpen: (open: boolean) => void;
}

const SortableTabItem: React.FC<SortableTabItemProps> = ({
  tab,
  index,
  handleTabChange,
  handleDeleteTab,
  handleRemoveDataItem,
  setCurrentTabIndex,
  setIsProductModalOpen,
  setIsCollectionModalOpen,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `tab-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        border: '1px solid #d9d9d9',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#fafafa',
      }}>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: '#999',
            userSelect: 'none',
          }}>
          <DragOutlined />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>탭 이름</label>
            <input
              type="text"
              value={tab.title || ''}
              onChange={(e) => handleTabChange(index, 'title', e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                fontSize: '14px',
              }}
              placeholder="탭 제목을 입력하세요"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>타입</label>
            <Select
              value={tab.type || undefined}
              onChange={(value) => handleTabChange(index, 'type', value)}
              style={{ width: '100%' }}
              placeholder="타입을 선택하세요">
              <Select.Option value="vote">투표</Select.Option>
              <Select.Option value="review">후기</Select.Option>
              <Select.Option value="talk">톡톡</Select.Option>
              <Select.Option value="collection">기획전</Select.Option>
              <Select.Option value="product">상품</Select.Option>
              <Select.Option value="event">혜택과 참여</Select.Option>
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
              데이터 ({tab.data && tab.data.length > 0 ? `${tab.data.length}개` : '0개'})
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tab.data && tab.data.length > 0 && !['vote', 'review', 'talk', 'event'].includes(tab.type) ? (
                <div
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}>
                  {tab.data.map((item, dataIndex) => (
                    <div
                      key={dataIndex}
                      style={{
                        padding: '8px 12px',
                        borderBottom: dataIndex < (tab.data?.length || 0) - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '2px' }}>
                          {item.title || '제목 없음'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          ID: {item.payload?.productId || item.payload?.collectionId}
                        </div>
                      </div>
                      <Button
                        size="small"
                        danger
                        onClick={() => handleRemoveDataItem(index, dataIndex)}
                        style={{ marginLeft: '8px' }}>
                        삭제
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: '16px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px',
                  }}>
                  {TAB_TYPE_DESCRIPTIONS[tab.type || '']}
                </div>
              )}
              {tab.type === 'product' && (
                <Button
                  onClick={() => {
                    setCurrentTabIndex(index);
                    setIsProductModalOpen(true);
                  }}>
                  상품 추가
                </Button>
              )}
              {tab.type === 'collection' && (
                <Button
                  onClick={() => {
                    setCurrentTabIndex(index);
                    setIsCollectionModalOpen(true);
                  }}>
                  기획전 추가
                </Button>
              )}
            </div>
          </div>
        </div>
        <Button danger onClick={() => handleDeleteTab(index)} style={{ marginTop: '24px' }}>
          삭제
        </Button>
      </div>
    </div>
  );
};

export const ShortcutV2Edit: React.FC = () => {
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const shortcutV2Query = useQuery({
    queryKey: ['shortcut-v2'],
    queryFn: () => api.getShortcutV2(),
  });

  const [tabs, setTabs] = useState<FeedData<FeedDataItem>[]>([]);
  const [shortcutActive, setShortcutActive] = useState(false);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(-1);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const updateShortcutV2Mutation = useMutation({
    mutationFn: api.updateShortcutV2,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shortcut-v2'] });
      dialog.open({ type: 'ok', title: '알림', content: '저장 되었습니다.' });
      setIsModalOpen(false);
    },
    onError: async (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e.message || JSON.stringify(e) });
    },
  });

  useEffect(() => {
    const feed = shortcutV2Query.data?.data;
    if (feed?.data?.[0]) {
      const shortcutConfig = feed.data[0];
      const shortcutData = Array.isArray(shortcutConfig.data) ? shortcutConfig.data : [];
      setTabs(shortcutData);
      setShortcutActive(shortcutConfig.active ?? false);
    }
  }, [shortcutV2Query.data]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = tabs?.findIndex((_, idx) => `tab-${idx}` === active.id);
      const newIndex = tabs?.findIndex((_, idx) => `tab-${idx}` === over.id);
      setTabs((prevTabs) => arrayMove(prevTabs, oldIndex, newIndex));
    }
  };

  const handleAddTab = () => {
    const newTab: FeedData<FeedDataItem> = {
      type: '',
      title: '새 탭',
      data: [],
    };
    setTabs((prev) => [...prev, newTab]);
  };

  const handleTabChange = (index: number, field: 'title' | 'type', value: string) => {
    const newTabs = [...tabs];
    if (field === 'type') {
      // 타입이 변경될 때 데이터 초기화
      newTabs[index] = { ...newTabs[index], [field]: value, data: [] };
    } else {
      newTabs[index] = { ...newTabs[index], [field]: value };
    }
    setTabs(newTabs);
  };

  const handleDeleteTab = (index: number) => {
    const newTabs = [...tabs];
    newTabs.splice(index, 1);
    setTabs(newTabs);
  };

  const defaultTabIndex = tabs.findIndex((tab) => tab.defaultSelected);
  const defaultTabValue = defaultTabIndex >= 0 ? String(defaultTabIndex) : 'all';

  const handleDefaultTabChange = (value: string) => {
    if (value === 'all') {
      setTabs((prev) => prev.map((tab) => ({ ...tab, defaultSelected: false })));
      return;
    }
    const selectedIndex = Number(value);
    setTabs((prev) => prev.map((tab, index) => ({ ...tab, defaultSelected: index === selectedIndex })));
  };

  const handleAddProducts = (index: number, products: any[]) => {
    const newTabs = [...tabs];
    const currentData = newTabs[index].data || [];
    const newProducts = products.map((product) => ({
      action: DeepLinkActions.GO_TO_PRODUCT,
      payload: {
        productId: product._id,
        originFee: product.originFee || 0,
        fee: product?.discountInfo?.discountedFee || product.fee || 0,
        discount: product.discount || '',
      },
      type: 'product',
      title: product.title || '',
      imageUrl: product?.thumbnailImageUrl || product?.detailImages?.[0] || product?.detailBodyImages?.[0] || '',
    }));

    const totalCount = currentData.length + newProducts.length;
    if (totalCount > 10) {
      alert(
        `최대 10개까지만 추가할 수 있습니다. (현재: ${currentData.length}개, 추가하려는 항목: ${newProducts.length}개)`,
      );
      return;
    }

    newTabs[index] = { ...newTabs[index], data: [...currentData, ...newProducts] };
    setTabs(newTabs);
  };

  const handleAddCollections = (index: number, collections: any[]) => {
    const newTabs = [...tabs];
    const currentData = newTabs[index].data || [];
    const newCollections = collections.map((collection) => ({
      action: DeepLinkActions.GO_TO_COLLECTION,
      payload: {
        collectionId: collection._id,
      },
      type: 'collection',
      title: collection.title || '',
      imageUrl: collection?.mainBannerImageUrl || collection?.coverImageUrl || '',
    }));

    const totalCount = currentData.length + newCollections.length;
    if (totalCount > 10) {
      alert(
        `최대 10개까지만 추가할 수 있습니다. (현재: ${currentData.length}개, 추가하려는 항목: ${newCollections.length}개)`,
      );
      return;
    }

    newTabs[index] = { ...newTabs[index], data: [...currentData, ...newCollections] };
    setTabs(newTabs);
  };

  const handleRemoveDataItem = (tabIndex: number, dataIndex: number) => {
    const newTabs = [...tabs];
    const newData = [...(newTabs[tabIndex].data || [])];
    newData.splice(dataIndex, 1);
    newTabs[tabIndex] = { ...newTabs[tabIndex], data: newData };
    setTabs(newTabs);
  };

  const handleSave = async () => {
    const formData = new FormData();
    const feedData: api.ShortcutV2Feed = {
      type: 'shortcut-v2',
      data: [
        {
          type: 'shortcut-v2',
          data: [...tabs],
          active: shortcutActive,
        },
      ],
    };
    formData.append('json', JSON.stringify(feedData));
    await updateShortcutV2Mutation.mutateAsync(formData);
  };

  return (
    <>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>숏컷 설정(발견)</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={() => {
                setIsModalOpen(true);
              }}>
              수정
            </Button>
          </div>
        </div>
      </div>

      <Modal
        width={800}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={
          <div>
            <Button type="primary" loading={updateShortcutV2Mutation.isPending} onClick={handleSave}>
              저장
            </Button>
            <Button onClick={() => setIsModalOpen(false)}>취소</Button>
          </div>
        }>
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>숏컷</span>
            <Switch
              checked={shortcutActive}
              onChange={setShortcutActive}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>
          <Button onClick={handleAddTab}>탭 추가</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>기본 노출</span>
            <Select value={defaultTabValue} onChange={handleDefaultTabChange} style={{ width: 180 }}>
              <Select.Option value="all">전체</Select.Option>
              {tabs.map((tab, index) => (
                <Select.Option key={`default-tab-${index}`} value={String(index)}>
                  {tab.title || '이름 없음'}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tabs.map((_, idx) => `tab-${idx}`)} strategy={verticalListSortingStrategy}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '60vh', overflowY: 'auto' }}>
              {tabs.map((tab, index) => (
                <SortableTabItem
                  key={`tab-${index}`}
                  tab={tab}
                  index={index}
                  handleTabChange={handleTabChange}
                  handleDeleteTab={handleDeleteTab}
                  handleRemoveDataItem={handleRemoveDataItem}
                  setCurrentTabIndex={setCurrentTabIndex}
                  setIsProductModalOpen={setIsProductModalOpen}
                  setIsCollectionModalOpen={setIsCollectionModalOpen}
                />
              ))}
              {tabs.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px', color: '#999' }}>
                  탭이 없습니다. "탭 추가" 버튼을 클릭하여 새 탭을 추가하세요.
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </Modal>

      <SelectProductModal
        open={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        selectLimit={10}
        onAddProducts={(products) => {
          if (currentTabIndex >= 0) {
            handleAddProducts(currentTabIndex, products);
          }
        }}
      />

      <SelectProductCollectionModal
        open={isCollectionModalOpen}
        onClose={() => setIsCollectionModalOpen(false)}
        onAddCollections={(collections) => {
          if (currentTabIndex >= 0) {
            handleAddCollections(currentTabIndex, collections);
          }
        }}
      />
    </>
  );
};

type MainFeedV2ConfigItem = {
  type: 'campaign';
  campaignId?: string;
  active?: boolean;
};

type MainFeedV2Config = {
  type: 'main-feed-v2-config';
  data?: MainFeedV2ConfigItem[];
  [key: string]: any;
};

const BannersEdit: React.FC = () => {
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bannersData, setBannersData] = useState<Partial<FeedData<FeedDataItem>>>({
    type: 'banners',
    shuffle: false,
    autoplay: false,
    delay: 4,
    data: [],
  });
  const [modifiedFiles, setModifiedFiles] = useState<{ [key: string]: File }>({});
  const bannerEditRef = React.useRef<BannerEditHandle>(null);

  const bannersQuery = useQuery({
    queryKey: ['main-feed-v2-banners'],
    queryFn: () => api.getMainFeedV2Banners(),
  });

  const updateBannersMutation = useMutation({
    mutationFn: api.updateMainFeedV2Banners,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['main-feed-v2-banners'] });
      dialog.open({ type: 'ok', title: '알림', content: '대배너를 저장했습니다.' });
      setModifiedFiles({});
      setIsModalOpen(false);
    },
    onError: async (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  useEffect(() => {
    if (bannersQuery.data?.data) {
      setBannersData({
        type: 'banners',
        shuffle: bannersQuery.data.data.shuffle ?? false,
        autoplay: bannersQuery.data.data.autoplay ?? false,
        delay: bannersQuery.data.data.delay ?? 4,
        data: bannersQuery.data.data.data || [],
      });
    }
  }, [bannersQuery.data]);

  const resetFromQuery = () => {
    if (bannersQuery.data?.data) {
      setBannersData({
        type: 'banners',
        shuffle: bannersQuery.data.data.shuffle ?? false,
        autoplay: bannersQuery.data.data.autoplay ?? false,
        delay: bannersQuery.data.data.delay ?? 4,
        data: bannersQuery.data.data.data || [],
      });
    } else {
      setBannersData({ type: 'banners', shuffle: false, autoplay: false, delay: 4, data: [] });
    }
    setModifiedFiles({});
  };

  const handleSave = async () => {
    const formData = new FormData();
    const json = {
      ...bannersData,
      type: 'banners',
      data: (bannersData.data || []).map((item) => ({
        ...item,
        type: item.type || 'banner',
      })),
    };
    json.data = removeTempProperties(json.data);

    formData.append('json', JSON.stringify(json));
    Object.values(modifiedFiles).forEach((file) => {
      formData.append('files', file);
    });

    await updateBannersMutation.mutateAsync(formData);
  };

  return (
    <>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600 }}>대배너 설정(발견)</h3>
          <Button
            onClick={() => {
              resetFromQuery();
              setIsModalOpen(true);
            }}>
            수정
          </Button>
        </div>
      </div>

      <Modal
        width={760}
        centered
        closable={false}
        open={isModalOpen}
        onCancel={() => {
          resetFromQuery();
          setIsModalOpen(false);
        }}
        footer={null}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
          <Button onClick={() => bannerEditRef.current?.addEmptyBanner()}>빈 배너 추가</Button>
          <Button type="primary" loading={updateBannersMutation.isPending} onClick={handleSave}>
            저장
          </Button>
          <Button
            onClick={() => {
              resetFromQuery();
              setIsModalOpen(false);
            }}>
            닫기
          </Button>
        </div>
        <BannerEdit
          ref={bannerEditRef}
          clear={!isModalOpen}
          data={bannersData}
          onChange={(data) => {
            setBannersData(data);
          }}
          onChangeImageFile={(files) => {
            setModifiedFiles(files);
          }}
        />
      </Modal>
    </>
  );
};

const CampaignsEdit: React.FC = () => {
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: ['main-feed-v2-config'],
    queryFn: () => api.getMainFeedV2Config(),
  });

  const campaignsQuery = useQuery({
    queryKey: ['campaigns-for-main-feed-v2'],
    queryFn: async () => {
      const res = await campaignsApi.searchCampaigns({ page: 0, size: 200 });
      return res.data;
    },
  });

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>(undefined);
  const [selectedActive, setSelectedActive] = useState<boolean>(false);

  useEffect(() => {
    const config = (configQuery.data?.data || {}) as MainFeedV2Config;
    const item = (config.data || []).find((d: any) => d?.type === 'campaign') as MainFeedV2ConfigItem | undefined;
    if (item?.campaignId) {
      setSelectedCampaignId(item.campaignId);
      setSelectedActive(item.active === true);
    } else if (item?.campaignId === undefined) {
      // legacy: allow unset; keep UI empty unless we already chose something
    }
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (next: { campaignId?: string; active: boolean }) => {
      const existing = (configQuery.data?.data || {}) as MainFeedV2Config;
      const existingItems = Array.isArray(existing.data) ? existing.data : [];

      const nextItems = [
        ...existingItems.filter((d: any) => d?.type !== 'campaign'),
        { type: 'campaign', campaignId: next.campaignId, active: next.active } as MainFeedV2ConfigItem,
      ];

      const payload: MainFeedV2Config = {
        ...(existing || {}),
        type: 'main-feed-v2-config',
        data: nextItems,
      };

      return await api.updateMainFeedV2Config(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['main-feed-v2-config'] });
      dialog.open({ type: 'ok', title: '알림', content: '저장 되었습니다.' });
    },
    onError: async (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  const campaigns = campaignsQuery.data?.contents || [];
  const selectedCampaign: Campaign | undefined = campaigns.find((c: any) => c?._id === selectedCampaignId);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600 }}>캠페인 설정(메인피드 V2)</h3>
        <Button
          type="primary"
          loading={saveMutation.isPending}
          disabled={!selectedCampaignId}
          onClick={async () => {
            await saveMutation.mutateAsync({ campaignId: selectedCampaignId, active: selectedActive });
          }}>
          저장
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 120, fontSize: 14, fontWeight: 500 }}>활성화</div>
          <Switch
            checked={selectedActive}
            disabled={!selectedCampaignId || saveMutation.isPending}
            checkedChildren="ON"
            unCheckedChildren="OFF"
            onChange={(checked) => setSelectedActive(checked)}
          />
          <div style={{ fontSize: 12, color: '#666' }}>
            {selectedCampaignId ? (selectedActive ? '활성' : '비활성') : '-'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 120, fontSize: 14, fontWeight: 500 }}>캠페인 선택</div>
          <Select
            style={{ flex: 1 }}
            placeholder="저장할 캠페인을 선택하세요"
            loading={campaignsQuery.isLoading}
            value={selectedCampaignId}
            options={campaigns.map((c: any) => ({
              value: c._id,
              label: `${c.title || '(제목 없음)'}${c.active === false ? ' (비활성)' : ''}`,
            }))}
            onChange={(value) => {
              setSelectedCampaignId(value);
              setSelectedActive(false);
            }}
          />
        </div>

        <div style={{ fontSize: 12, color: '#666' }}>
          현재 저장된 값:{' '}
          {selectedCampaignId ? (
            <span style={{ color: '#333' }}>
              {selectedCampaign?.title || selectedCampaignId} {selectedCampaign?.active === false ? '(비활성)' : ''}
            </span>
          ) : (
            <span>-</span>
          )}
        </div>

        {configQuery.isLoading && <div style={{ fontSize: 12, color: '#999' }}>저장된 설정을 불러오는 중...</div>}
        {configQuery.isError && <div style={{ fontSize: 12, color: '#bd334a' }}>설정 조회 중 오류가 발생했습니다.</div>}
      </div>
    </div>
  );
};

const MainFeedV2Page: React.FC = () => {
  const [categories, setCategories] = useState<FeedData<FeedDataItem>[]>([]);
  const [modifiedFiles, setModifiedFiles] = useState<{ [key: string]: File }>({});
  const [weights, setWeights] = useState<ProductRecommendationWeights>({
    paymentCount: 0,
    viewCount: 0,
    reviewRating: 0,
    reviewCount: 0,
    favoritedCount: 0,
    newness: 0,
    recentRootCategory: 0,
  });
  const [initialWeights, setInitialWeights] = useState<ProductRecommendationWeights>({
    paymentCount: 0,
    viewCount: 0,
    reviewRating: 0,
    reviewCount: 0,
    favoritedCount: 0,
    newness: 0,
    recentRootCategory: 0,
  });
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();

  const queryKey = 'product-root-categories';

  const { data: productRootCategoriesQuery } = useQuery({
    queryKey: [queryKey],
    queryFn: api.getProductRootCategories,
  });

  const updateProductRootCategoriesMutation = useMutation({
    mutationFn: api.updateProductRootCategories,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKey] });
      dialog.open({ type: 'ok', title: '알림', content: '상품 카테고리를 저장했습니다.' });
      setModifiedFiles({});
    },
    onError: async (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e.message || JSON.stringify(e) });
    },
  });

  const { data: productRecommendationWeightsQuery } = useQuery({
    queryKey: ['product-recommendation-weights'],
    queryFn: api.getProductRecommendationWeights,
  });

  const updateProductRecommendationWeightsMutation = useMutation({
    mutationFn: api.updateProductRecommendationWeights,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['product-recommendation-weights'] });
      dialog.open({ type: 'ok', title: '알림', content: '가중치를 저장했습니다.' });
    },
    onError: async (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e.message || JSON.stringify(e) });
    },
  });

  const deployFeedMutation = useMutation({
    mutationFn: () => feedsApi.deployFeed('main-feed'),
    onSuccess: async () => {
      dialog.open({
        type: 'ok',
        title: '알림',
        content: '메인피드를 운영에 배포했습니다.',
      });
    },
    onError: async (e) => {
      dialog.open({ type: 'ok', title: '오류', content: JSON.stringify(e) });
    },
  });

  useEffect(() => {
    if (productRootCategoriesQuery) {
      setCategories(productRootCategoriesQuery?.data?.data || []);
    }
  }, [productRootCategoriesQuery]);

  useEffect(() => {
    if (productRecommendationWeightsQuery?.data) {
      const weightsData = productRecommendationWeightsQuery.data;
      setWeights(weightsData);
      setInitialWeights(weightsData);
    }
  }, [productRecommendationWeightsQuery]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = categories?.findIndex((c) => c.type === active.id);
      const newIndex = categories?.findIndex((c) => c.type === over.id);
      setCategories((cats) => arrayMove(cats, oldIndex, newIndex));
    }
  };

  const handleChange = (index: number, changedItem: FeedData<FeedDataItem>) => {
    const newCategories = [...(categories || [])];
    newCategories[index] = changedItem;
    setCategories(newCategories);
  };

  const handleDelete = (index: number) => {
    const newCategories = [...(categories || [])];
    newCategories.splice(index, 1);
    setCategories(newCategories);
  };

  const handleAdd = () => {
    const newKey = `new-category-${Date.now()}`;
    const newCategory: FeedData<FeedDataItem> = {
      type: newKey,
      label: { ko: '', en: '' },
      imageUrl: '',
    };
    setCategories((cats) => [...(cats || []), newCategory]);
  };

  const handleImageChange = (index: number, file: File) => {
    const newCategories = [...categories];
    const item = newCategories[index];
    const tempUrl = URL.createObjectURL(file);
    const newFileName = `${item.type || `new_${index}`}.${file.name.split('.').pop()}`;

    newCategories[index] = { ...item, imageUrl: newFileName, imageUrlTemp: tempUrl };
    setCategories(newCategories);
    setModifiedFiles((prev) => ({ ...prev, [newFileName]: file }));
  };

  const handleSave = async () => {
    // Key uniqueness validation
    const keys = categories?.map((c) => c.type);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      dialog.open({ type: 'ok', title: '오류', content: '모든 카테고리의 Key는 고유해야 합니다.' });
      return;
    }

    // Required fields validation
    for (const category of categories || []) {
      if (!category.type || !category.label?.ko || !category.imageUrl) {
        dialog.open({
          type: 'ok',
          title: '오류',
          content: `"${
            category.label?.ko || category.type
          }" 항목의 필수 필드(Key, 라벨(한국어), 아이콘)를 모두 채워주세요.`,
        });
        return;
      }
    }

    const formData = new FormData();
    const cleanData = removeTempProperties(
      categories?.map((category) => {
        const newCategory = { ...category };
        delete newCategory.imageUrlTemp;

        return newCategory;
      }),
    );
    formData.append('json', JSON.stringify(cleanData));

    Object.entries(modifiedFiles).forEach(([fileName, file]) => {
      formData.append('files', file, fileName);
    });

    // Save categories
    await updateProductRootCategoriesMutation.mutateAsync(formData);

    // Check if weights have changed
    const weightsChanged =
      weights &&
      initialWeights &&
      (weights.paymentCount !== initialWeights.paymentCount ||
        weights.viewCount !== initialWeights.viewCount ||
        weights.reviewRating !== initialWeights.reviewRating ||
        weights.reviewCount !== initialWeights.reviewCount ||
        weights.favoritedCount !== initialWeights.favoritedCount ||
        weights.newness !== initialWeights.newness ||
        weights.recentRootCategory !== initialWeights.recentRootCategory);

    // Save weights if changed
    if (weightsChanged && weights) {
      await updateProductRecommendationWeightsMutation.mutateAsync(weights);
      setInitialWeights(weights);
    }

    await deployFeedMutation.mutateAsync();
  };

  const handleWeightChange = (field: keyof ProductRecommendationWeights, value: number | null) => {
    if (value !== null && weights) {
      setWeights({ ...weights, [field]: value });
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>상품 카테고리 관리</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleAdd}>항목 추가</Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={
              updateProductRootCategoriesMutation.isPending || updateProductRecommendationWeightsMutation.isPending
            }>
            저장
          </Button>
        </div>
      </div>
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '32px', overflow: 'auto' }}>
        {/* 카테고리 설정 */}
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>카테고리 설정</h3>
          <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={categories?.map((c) => c.type || '')} strategy={horizontalListSortingStrategy}>
                <div style={{ display: 'flex', gap: '16px', padding: '16px 0' }}>
                  {categories?.map((item, index) => (
                    <ProductCategoryItem
                      key={index}
                      id={item.type}
                      item={item}
                      onChange={(changedItem) => handleChange(index, changedItem)}
                      onChangeImage={(file) => handleImageChange(index, file)}
                      onDeleteItem={() => handleDelete(index)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
        {/* 가중치 설정 */}
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>가중치 설정</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={weightItemStyle}>
              <label style={labelStyle}>결제 횟수</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.paymentCount || 0}
                onChange={(value) => handleWeightChange('paymentCount', value)}
                step={0.1}
                precision={2}
              />
            </div>
            <div style={weightItemStyle}>
              <label style={labelStyle}>조회수</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.viewCount || 0}
                onChange={(value) => handleWeightChange('viewCount', value)}
                step={0.1}
                precision={2}
              />
            </div>
            <div style={weightItemStyle}>
              <label style={labelStyle}>리뷰 평점</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.reviewRating || 0}
                onChange={(value) => handleWeightChange('reviewRating', value)}
                step={0.1}
                precision={2}
              />
            </div>
            <div style={weightItemStyle}>
              <label style={labelStyle}>리뷰 개수</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.reviewCount || 0}
                onChange={(value) => handleWeightChange('reviewCount', value)}
                step={0.1}
                precision={2}
              />
            </div>
            <div style={weightItemStyle}>
              <label style={labelStyle}>찜 개수</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.favoritedCount}
                onChange={(value) => handleWeightChange('favoritedCount', value)}
                step={0.1}
                precision={2}
              />
            </div>
            <div style={weightItemStyle}>
              <label style={labelStyle}>최근 승인일</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.newness || 0}
                onChange={(value) => handleWeightChange('newness', value)}
                step={0.1}
                precision={2}
              />
            </div>
            <div style={weightItemStyle}>
              <label style={labelStyle}>최근 카테고리</label>
              <InputNumber
                style={{ width: '100px' }}
                value={weights.recentRootCategory || 0}
                onChange={(value) => handleWeightChange('recentRootCategory', value)}
                step={0.1}
                precision={2}
              />
            </div>
          </div>
        </div>
        {/* 숏컷 설정 */}
        <ShortcutFeedEdit />
        {/* 발견 > 숏컷 */}
        <ShortcutV2Edit />
        {/* 발견 > 대배너 설정 */}
        <BannersEdit />
        {/* 발견 > 캘린더 행사 설정 */}
        <CalendarEventsEdit />
        {/* 캠페인 설정 */}
        <CampaignsEdit />
      </div>
    </div>
  );
};

const weightItemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
};

export default MainFeedV2Page;
